import React from "react";
import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
} from "@common/constants";
import { Entity } from "@common/types/entity";
import { ReportFilters } from "@common/types/filters/reports";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { HStack } from "@design-system/HStack/HStack";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import styles from "./ReportsPageFilters.module.scss";

const getLabel = (cycle: AccountingReportCycleOptionType) => {
    switch (cycle) {
        case "month":
            return "By month";
        case "week":
            return "By week";
        case "year":
            return "By year";
        default:
            return "";
    }
};

const cycleOptions: Array<PickerItemData<AccountingReportCycleOptionType>> = [
    {
        value: AccountingReportCycle.WEEK,
        label: getLabel(AccountingReportCycle.WEEK),
    },
    {
        value: AccountingReportCycle.MONTH,
        label: getLabel(AccountingReportCycle.MONTH),
    },
    {
        value: AccountingReportCycle.YEAR,
        label: getLabel(AccountingReportCycle.YEAR),
    },
];

interface ReportsPageFiltersProps {
    filters: ReportFilters;
    onChange(filters: Partial<ReportFilters>): void;
    hardcodedEntity?: Entity;
    pinnedPresets?: readonly string[];
    onTogglePin?: (id: string) => void;
    actions?: React.ReactNode;
    exportButton?: React.ReactNode;
}

export const ReportsPageFilters: React.FC<ReportsPageFiltersProps> = ({
    filters,
    onChange,
    hardcodedEntity,
    pinnedPresets,
    onTogglePin,
    actions,
    exportButton,
}) => (
    <section className="reports-filter-form">
        <div className="reports-filter-form__nav"></div>
        <div className="reports-filter-form__filters">
            <HStack gap={12}>
                {actions}
                <div className={styles.filterGroup}>
                    <DateRangeFilterNext
                        dateRange={toDateOnlyDateRange({
                            start: filters.start,
                            end: filters.end,
                        })}
                        onChange={(value, isAllTime) => {
                            if (isAllTime) {
                                onChange({});
                                return;
                            }
                            const localRange = toLocalDateRange(value);
                            onChange({
                                start: localRange.start,
                                end: localRange.end,
                            });
                        }}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                        buttonClass={styles.dateRangeFilter}
                    />
                    <Picker.Select
                        items={cycleOptions}
                        value={filters.cycle}
                        onValueChange={(cycle) => cycle && onChange({ cycle })}
                        renderValue={() => getLabel(filters.cycle)}
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                className={styles.cycleSelect}
                            />
                        }
                    />
                </div>
            </HStack>
            {hardcodedEntity ? (
                <Button
                    variant="primary"
                    size="md"
                    leftIcon={
                        <span data-slot-box="">
                            <EntityAvatar
                                entity={hardcodedEntity}
                                size="xs"
                                aria-hidden
                            />
                        </span>
                    }
                >
                    <span className="reports-filter-form__hardcoded-entity__name">
                        {hardcodedEntity.name}
                    </span>
                </Button>
            ) : (
                <EntityFilterCombobox
                    filters={filters}
                    onlyBusiness
                    onChange={onChange}
                />
            )}
            {exportButton}
        </div>
    </section>
);
